import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import { CommonLayout, MobileNumberField } from './commonComponents';
import { MFSubmitButton, MFTextField } from '../lib/formik';
import MFSelectField from '../lib/formik/SelectField';
import { showError } from '../redux-store/actions/auth';
import {
  adminSupportMaster,
  ENABLE_PROCESS_SIGNING_APPLICATIONS,
  ENABLE_TOPUP_APPLICATION_ADMIN_SUPPORT,
  ENABLE_CREATE_TOPUP_AND_MOVE_DISTRIBUTOR,
  SUPPORT_STATUS,
} from '../utils/constant';
import { adminSupportValidation } from '../utils/schema';
import { getDistributor, preventSpecialCharacters } from '../utils/utilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../redux-store/reducers';
import {
  unlockUser,
  checkWhitelistUser,
  whitelistUser,
  checkWhitelistedResponceBody,
  supportTypeBody,
  unlockAccess,
  WhitelistingAccess,
  unlockAccessBody,
  whitelistAccessBody,
  changeStatusAccess,
  statusAccessBody,
  changeStatusToDraft,
  changeStatusFromExpiredexport,
  processSignedApplications,
  makeCompletedApplications,
  DeleteTopUpApplications,
  topUpMoveDistributor,
} from '../redux-store/actions/adminSupport';
import { nonDigitRegex } from '../utils/regex';
import { DistributorListType, SubDistributorListType } from '../redux-store/types/api-types';
import { getDistributorsListForFilters } from '../redux-store/actions';
import { getDistributorById } from '../redux-store/actions/userManagement';

export interface adminSupportValues {
  supportType: string;
  phone: string;
  countryCode: string;
  application_number: string;
  status: string;
  distributor: string;
  subDistributor: string;
  distributorEmail: string;
  enableSubDistributor: boolean;
  arnCode: string;
  distributorPhone: string;
}

const adminSupportInitialValues: adminSupportValues = {
  supportType: '',
  phone: '',
  countryCode: 'India: +91',
  status: '',
  application_number: '',
  distributor: '',
  subDistributor: '',
  distributorEmail: '',
  arnCode: '',
  enableSubDistributor: false,
  distributorPhone: '',
};

export default function adminSupport(): JSX.Element {
  const dispatch = useDispatch();
  const [unlockAcc, setUnlockAcc] = useState(false);
  const [whitelistAccess, setWhitelistAccess] = useState(false);
  const [statusAccess, setStatusAccess] = useState(false);
  const { adminSupport } = useSelector((store: RootStateType) => store.auth);
  const [distributorData, setDistributorData] = useState<DistributorListType[]>([]);
  const [subDistributorData, setSubDistributorData] = useState<SubDistributorListType[]>([]);

  useEffect(() => {
    (async function () {
      try {
        if (adminSupport) {
          const unlockAccessResponse = (await dispatch(
            unlockAccess()
          )) as unknown as unlockAccessBody;
          setUnlockAcc(unlockAccessResponse.unlockingAccess);
          const whitelistAccessResponse = (await dispatch(
            WhitelistingAccess()
          )) as unknown as whitelistAccessBody;
          setWhitelistAccess(whitelistAccessResponse.whitelistingAccess);
          const statusAccessResponse = (await dispatch(
            changeStatusAccess()
          )) as unknown as statusAccessBody;
          setStatusAccess(statusAccessResponse.applicationStatusChangeAccess);
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);

  const supportOptions = Object.keys(adminSupportMaster).filter((item) => {
    if (unlockAcc && adminSupportMaster[item] === adminSupportMaster.unlock_user) {
      return item;
    }
    if (
      whitelistAccess &&
      [
        adminSupportMaster.check_whitelisted,
        adminSupportMaster.whitelist_user,
        adminSupportMaster.unwhitelist_user,
      ].includes(adminSupportMaster[item])
    ) {
      return item;
    }
    if (
      statusAccess &&
      [
        adminSupportMaster.change_expired_status,
        adminSupportMaster.change_status_to_draft,
      ].includes(adminSupportMaster[item])
    ) {
      return item;
    }
    if (
      ENABLE_PROCESS_SIGNING_APPLICATIONS &&
      adminSupportMaster[item] === adminSupportMaster.process_signed_applications
    ) {
      return item;
    }
    if (
      ENABLE_TOPUP_APPLICATION_ADMIN_SUPPORT &&
      [
        adminSupportMaster.delete_topup_application,
        adminSupportMaster.application_complete_for_topup,
      ].includes(adminSupportMaster[item])
    ) {
      return item;
    }
    if (
      ENABLE_CREATE_TOPUP_AND_MOVE_DISTRIBUTOR &&
      adminSupportMaster[item] === adminSupportMaster.create_topup_move_distributor
    ) {
      return item;
    }
    return;
  });

  const handleSubmit = async (values: adminSupportValues) => {
    try {
      if (adminSupportMaster[values.supportType] === adminSupportMaster.unlock_user) {
        const unlockResponse = (await dispatch(
          unlockUser({ phone: values.phone, countryCode: values.countryCode?.split(':')[1].trim() })
        )) as unknown as supportTypeBody;
        throw `${unlockResponse.message}`;
      }
      if (adminSupportMaster[values.supportType] === adminSupportMaster.check_whitelisted) {
        const checkWhitelistResponse = (await dispatch(
          checkWhitelistUser({
            phone: values.phone,
            countryCode: values.countryCode?.split(':')[1].trim(),
          })
        )) as unknown as checkWhitelistedResponceBody;
        throw `${
          checkWhitelistResponse.isMobileNumberWhiteListed
            ? 'This Number is Whitelisted'
            : 'This Number is not Whitelisted'
        }`;
      }
      if (adminSupportMaster[values.supportType] === adminSupportMaster.change_expired_status) {
        const changeStatusFromExpired = (await dispatch(
          changeStatusFromExpiredexport({
            applicationStatus: values.status,
            applicationNumber: values.application_number,
          })
        )) as unknown as supportTypeBody;
        throw `${changeStatusFromExpired.message}`;
      }
      if (adminSupportMaster[values.supportType] === adminSupportMaster.change_status_to_draft) {
        const changeStatusToDraftRes = (await dispatch(
          changeStatusToDraft({
            applicationNumber: values.application_number,
            deleteLeegalityDocument: true,
          })
        )) as unknown as supportTypeBody;
        throw `${changeStatusToDraftRes.message}`;
      }
      if (
        [adminSupportMaster.whitelist_user, adminSupportMaster.unwhitelist_user].includes(
          adminSupportMaster[values.supportType]
        )
      ) {
        const whitelistResponse = (await dispatch(
          whitelistUser({
            phone: values.phone,
            countryCode: values.countryCode?.split(':')[1].trim(),
            whiteList:
              adminSupportMaster[values.supportType] === adminSupportMaster.whitelist_user
                ? true
                : false,
          })
        )) as unknown as supportTypeBody;
        throw `${whitelistResponse.message}`;
      }
      if (
        ENABLE_PROCESS_SIGNING_APPLICATIONS &&
        adminSupportMaster[values.supportType] === adminSupportMaster.process_signed_applications
      ) {
        await dispatch(processSignedApplications());
        throw `Process Signed Applications Successfully`;
      }
      if (
        ENABLE_TOPUP_APPLICATION_ADMIN_SUPPORT &&
        adminSupportMaster[values.supportType] === adminSupportMaster.application_complete_for_topup
      ) {
        const res = (await dispatch(
          makeCompletedApplications({ applicationNumber: values.application_number })
          // eslint-disable-next-line
        )) as any;
        throw await res?.text();
      }
      if (
        ENABLE_TOPUP_APPLICATION_ADMIN_SUPPORT &&
        adminSupportMaster[values.supportType] === adminSupportMaster.delete_topup_application
      ) {
        // eslint-disable-next-line
        const res = (await dispatch(DeleteTopUpApplications())) as any;
        throw await res?.text();
      }
      if (
        ENABLE_CREATE_TOPUP_AND_MOVE_DISTRIBUTOR &&
        adminSupportMaster[values.supportType] === adminSupportMaster.create_topup_move_distributor
      ) {
        // eslint-disable-next-line
        const res = (await dispatch(
          topUpMoveDistributor({
            applicationNumber: values.application_number,
            distributorId: values.distributor ? Number(values.distributor) : null,
            subdistributorId: values.subDistributor ? Number(values.subDistributor) : null,
          })
        )) as unknown as supportTypeBody;
        throw res?.message;
      }
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e));
      console.error((e as Error).message);
    }
  };
  // eslint-disable-next-line
  const onSupportTypeChange = async (supportType: any, values: any, setValues: any) => {
    if (adminSupportMaster[supportType] === adminSupportMaster.change_expired_status) {
      setValues({
        ...values,
        supportType: supportType,
        phone: '',
        distributor: '',
        subDistributor: '',
        distributorEmail: '',
        arnCode: '',
        distributorPhone: '',
      });
      return;
    }

    if (adminSupportMaster[supportType] === adminSupportMaster.change_status_to_draft) {
      setValues({
        ...values,
        supportType: supportType,
        phone: '',
        status: '',
        distributor: '',
        subDistributor: '',
        distributorEmail: '',
        arnCode: '',
        distributorPhone: '',
      });
      return;
    }
    if (adminSupportMaster[supportType] === adminSupportMaster.create_topup_move_distributor) {
      const res = (await dispatch(
        getDistributorsListForFilters({ isActive: true })
      )) as unknown as DistributorListType[];
      setDistributorData(res);
      setValues({
        ...values,
        supportType: supportType,
        phone: '',
        status: '',
      });
      return;
    }

    setValues({
      ...values,
      supportType: supportType,
      application_number: '',
      status: '',
      distributor: '',
      subDistributor: '',
      distributorEmail: '',
      arnCode: '',
      distributorPhone: '',
    });
  };
  return (
    <CommonLayout>
      <Formik
        initialValues={adminSupportInitialValues}
        onSubmit={handleSubmit}
        validate={(values: adminSupportValues) => {
          // console.log(values);
          try {
            validateYupSchema(values, adminSupportValidation, true, values);
          } catch (e) {
            return yupToFormErrors(e);
          }
        }}
        enableReinitialize={true}>
        {({ handleSubmit, values, setValues }) => {
          return (
            <>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    fontWeight: 500,
                    mb: { xs: 2, md: 4 },
                    p: { xs: '5px 15px', md: '' },
                    bgcolor: '#e5f1f8',
                    borderRadius: '10px',
                  }}>
                  Admin Support
                </Typography>
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={10}
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}
                  sx={{
                    width: '100%',
                    ml: 0,
                    '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
                    p: '20px 30px',
                    bgcolor: 'common.white',
                    mt: 1.5,
                  }}>
                  <Grid sx={{ width: '100%' }} container rowSpacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Grid xs={12} sm={12}>
                        <MFSelectField
                          name={`supportType`}
                          label={'Support Type *'}
                          items={supportOptions.map((item) => ({
                            key: adminSupportMaster[item],
                            value: item,
                          }))}
                          onChange={(e) => {
                            // setFieldValue('supportType', e.target.value);
                            onSupportTypeChange(e.target.value, values, setValues);
                          }}
                          applyLabelStyles={true}
                        />
                      </Grid>
                    </Grid>
                    {![
                      adminSupportMaster.change_expired_status,
                      adminSupportMaster.change_status_to_draft,
                      adminSupportMaster.process_signed_applications,
                      adminSupportMaster.delete_topup_application,
                      adminSupportMaster.application_complete_for_topup,
                      adminSupportMaster.create_topup_move_distributor,
                    ].includes(adminSupportMaster[values.supportType]) && (
                      <Grid item xs={12} sm={6}>
                        <MobileNumberField
                          label="Mobile Number *"
                          name="phone"
                          placeholder="Enter Mobile number"
                          onKeyDown={(e) => {
                            preventSpecialCharacters(e);
                          }}
                          countryCodeFieldName={'countryCode'}
                          countryCodeValue={values.countryCode}
                          regexForFilterValue={nonDigitRegex}
                        />
                      </Grid>
                    )}
                    {[
                      adminSupportMaster.change_expired_status,
                      adminSupportMaster.change_status_to_draft,
                      adminSupportMaster.create_topup_move_distributor,
                    ].includes(adminSupportMaster[values.supportType]) && (
                      <Grid item xs={12} sm={6}>
                        <MFTextField
                          name={`application_number`}
                          label={`${
                            adminSupportMaster[values.supportType] ===
                            adminSupportMaster.create_topup_move_distributor
                              ? 'Application Number *'
                              : 'Application Number / Topup Number *'
                          }`}
                          placeholder={`Enter Application Number${
                            [
                              adminSupportMaster.change_expired_status,
                              adminSupportMaster.change_status_to_draft,
                            ].includes(adminSupportMaster[values.supportType])
                              ? ' / Topup Number'
                              : ''
                          }`}
                        />
                      </Grid>
                    )}
                    {[adminSupportMaster.create_topup_move_distributor].includes(
                      adminSupportMaster[values.supportType]
                    ) && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Grid xs={12} sm={12}>
                            <MFSelectField
                              name={`distributor`}
                              label={'Distributor *'}
                              items={distributorData.map((item) => ({
                                key: item.name,
                                value: item.id,
                              }))}
                              applyLabelStyles={true}
                              onChange={async (e) => {
                                const selectedDistributor = getDistributor(
                                  e.target.value as string,
                                  distributorData
                                ) as DistributorListType;
                                const isDistributorIndividual =
                                  selectedDistributor?.type === 'individual';
                                // setSubDistributorData(selectedDistributor?.subdistributors || []);
                                const res = (await dispatch(
                                  getDistributorById(e.target.value as number)
                                )) as unknown as DistributorListType;
                                if (!isDistributorIndividual) {
                                  setSubDistributorData(
                                    res.subdistributors?.filter((dist) => dist?.isActive)
                                  );
                                }
                                // setFieldValue('supportType', e.target.value);
                                setValues({
                                  ...values,
                                  distributor: e.target.value as string,
                                  subDistributor: '',
                                  distributorEmail: res?.user?.email || '',
                                  distributorPhone: res?.user?.phone || '',
                                  enableSubDistributor: !isDistributorIndividual,
                                  arnCode: selectedDistributor?.arnCode,
                                });
                              }}
                            />
                          </Grid>
                        </Grid>
                        {values.distributor && (
                          <Grid item xs={12} sm={6}>
                            <MFTextField
                              name={`arnCode`}
                              label={`Distributor Code*`}
                              placeholder="Enter Distributor Code"
                              disabled={true}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    {[adminSupportMaster.application_complete_for_topup].includes(
                      adminSupportMaster[values.supportType]
                    ) && (
                      <Grid item xs={12} sm={6}>
                        <MFTextField
                          name={`application_number`}
                          label={`Application ID *`}
                          placeholder="Enter Application ID"
                        />
                      </Grid>
                    )}
                    {adminSupportMaster[values.supportType] ===
                      adminSupportMaster.change_expired_status && (
                      <Grid item xs={12} sm={6}>
                        <Grid xs={12} sm={12}>
                          <MFSelectField
                            name={`status`}
                            label={'Status'}
                            items={Object.keys(SUPPORT_STATUS).map((item) => ({
                              key: SUPPORT_STATUS[item],
                              value: item,
                            }))}
                            applyLabelStyles={true}
                          />
                        </Grid>
                      </Grid>
                    )}
                    {values.enableSubDistributor && values.distributor && (
                      <Grid item xs={12} sm={6}>
                        <Grid xs={12} sm={12}>
                          <MFSelectField
                            name={`subDistributor`}
                            label={'Sub Distributor *'}
                            items={subDistributorData.map((item) => ({
                              key: item.user.firstName,
                              value: item.id,
                            }))}
                            applyLabelStyles={true}
                            onChange={(e) => {
                              const selectedSubDistributor = getDistributor(
                                e.target.value as string,
                                subDistributorData
                              ) as SubDistributorListType;
                              // setFieldValue('supportType', e.target.value);
                              setValues({
                                ...values,
                                subDistributor: e.target.value as string,
                                distributorEmail: selectedSubDistributor?.user.email,
                                distributorPhone: selectedSubDistributor?.user?.phone,
                              });
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                    {((!values.enableSubDistributor && values.distributor) ||
                      (values.enableSubDistributor && values.subDistributor)) && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <MFTextField
                            label={`${values.subDistributor ? 'Sub ' : ''}Distributor Email *`}
                            name="distributorEmail"
                            placeholder="Enter Sub Distributor Email"
                            disabled={true}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MFTextField
                            label={`${
                              values.subDistributor ? 'Sub ' : ''
                            }Distributor Phone Number *`}
                            name="distributorPhone"
                            placeholder="Enter Distributor Phone Number"
                            disabled={true}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Box sx={{ width: '100%', maxWidth: '350px', mx: 'auto', mt: 5, mb: 5 }}>
                    <MFSubmitButton label="Submit" />
                  </Box>
                </Grid>
              </Box>
            </>
          );
        }}
      </Formik>
    </CommonLayout>
  );
}
